import { useState, useRef, useEffect } from 'react';
import { Send } from 'lucide-react';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
  streamingMode?: boolean;
  transcript?: string;
}

export const ChatInput = ({
  onSend,
  disabled = false,
  placeholder = 'Type your message...',
  streamingMode = false,
  transcript = ''
}: ChatInputProps) => {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (transcript) {
      setMessage(transcript);
    }
  }, [transcript]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '44px'; // Reset to base height
      const newHeight = Math.min(textareaRef.current.scrollHeight, 120);
      if (newHeight > 44) {
        textareaRef.current.style.height = `${newHeight}px`;
      }
    }
  }, [message]);

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSend(message.trim());
      setMessage('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };


  return (
    <div className="border-t border-gray-100 bg-white px-6 py-5">
      <div className="flex gap-3 items-end max-w-4xl mx-auto">
        <div className="flex-1 relative group mt-2" >
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            rows={1}
            className="w-full bg-white text-gray-900 rounded-2xl px-5 py-2 resize-none outline-none border-2 border-gray-200 focus:border-[#4c3794] focus:ring-4 focus:ring-purple-100 transition-all placeholder:text-gray-400 disabled:opacity-50 text-[15px] leading-relaxed shadow-sm overflow-y-hidden"
          />
      
        </div>

        <button
          onClick={handleSend}
          disabled={disabled || !message.trim()}
          className="flex-shrink-0 w-12 h-12 bg-[#4c3794] hover:bg-[#3a2a73] text-white rounded-2xl flex items-center justify-center transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#4c3794] shadow-lg shadow-purple-200/50 hover:shadow-xl hover:shadow-purple-300/50 active:scale-95 disabled:shadow-none"
          aria-label="Send message"
        >
          <Send className="w-5 h-5" strokeWidth={2.5} />
        </button>
      </div>

    </div>
  );
};
